import { EditorEvents } from '@tiptap/core'

import { useDebouncedCallback } from '@bettermode/common/hooks'

import { ComposerProvider } from '../../Composer/ComposerProvider.js'
import { useI18n } from '../../i18n/index.js'
import { useChatContext } from '../ChatContext/ChatContext.js'
import { ChatComposerExtensionConfig } from '../constants.js'
import { useChatPasteHandler } from '../hooks/useChatPasteHandler.js'
import { ComposerSubmitParams } from '../types.js'

import { ChatComposer } from './ChatComposer.js'

const DRAFT_SAVE_DEBOUNCE = 500

interface Props {
  onComposerSubmit: (props: ComposerSubmitParams) => Promise<void>
  onKeyDown: (event: KeyboardEvent) => void
  disabled: boolean
}

export const Composer = ({ onComposerSubmit, onKeyDown, disabled }: Props) => {
  const { $t } = useI18n()
  const { pasteHandler } = useChatPasteHandler()
  const { draftMessage, updateDraftContent, setEditorRef } = useChatContext()

  const onUpdate = useDebouncedCallback(
    ({ editor }: EditorEvents['update']) => {
      const content = editor.getHTML()
      updateDraftContent(content)
    },
    DRAFT_SAVE_DEBOUNCE,
  )

  return (
    <ComposerProvider
      placeholder={$t({
        defaultMessage: 'Type message...',
        id: 'Messaging.Composer.Placeholder',
      })}
      content={draftMessage.content}
      extensionConfig={ChatComposerExtensionConfig}
      pasteHandler={pasteHandler}
      editorOptions={{
        onUpdate,
        onCreate: ({ editor }) => {
          setEditorRef(editor)
        },
      }}
    >
      <ChatComposer
        onComposerSubmit={onComposerSubmit}
        onKeyDown={onKeyDown}
        disabled={disabled}
      />
    </ComposerProvider>
  )
}
